import { MeetingRoomAudienceType, MeetingRoomHighlightingState } from "@kalyzee/kast-web-sdk/websocket";
import { useMemo } from "react";
import { GroupData, GroupUserData } from "@kalyzee/escaet-ui";
import { isRoleGreaterThanOrEqual } from "@kalyzee/kast-web-sdk";
import { UserRole } from "@kalyzee/kast-web-sdk/http";
import { PopulatedMeetingRoom, PopulatedMeetingUser } from "../interfaces/populatedData";
import { userToEscaetUser } from "../helpers/meeting";

/**
 * This hook retrieves all the users that are involved with groups,
 * not only the ones who are enlisted but also the ones who are only in the audience
 * for now (did not enlist yet). There can also be cases of users being enlisted
 * in groups without belonging to the audience (due to admin action).
 * 
 * Backend requirements :
 * #0 : the room.audienceUserIds have to be the exact same for all rooms of the same batch
 *      so we can use the audienceUserIds of the first room to get the users
 */
export const useMixedGroupUsers = (
  users: PopulatedMeetingUser[],
  groups: PopulatedMeetingRoom[],
): PopulatedMeetingUser[] => useMemo(
  () => {
    if (!groups.length) return [];

    let userIds: string[] = [];
    const batchAudience = groups[0].room.audience;
    if (batchAudience?.type === MeetingRoomAudienceType.User) {
      if (batchAudience.values === "*") {
        console.warn("group batch audience is '*', it should not be");
      } else {
        userIds = [...batchAudience.values];
      }
    } else {
      console.warn("group batch audience type is not user, it should be");
    }

    groups.forEach(group => {
      if (group.room.enlistedUserIds) {
        // We also collect rooms enlistedUserIds, in case some users that are not in the audience
        // were forced in by admins (possible with update using force:true param)
        userIds.push(...group.room.enlistedUserIds);
      }
    });
    const userIdsSet = new Set(userIds);
    
    return users.filter(user => userIdsSet.has(user.id));
  },
  [users, groups],
);

////////////////////////////////////////////////////////////////////////////////////////////////
/////////////////////////////////////// ESCAET /////////////////////////////////////////////////
////////////////////////////////////////////////////////////////////////////////////////////////

const populatedUserToEscaetUser = (user: PopulatedMeetingUser, selfId?: string): GroupUserData | undefined => {
  return userToEscaetUser(user.user, user.sessions.length > 0, selfId);
}

export const usersToEscaetUsers = (users: PopulatedMeetingUser[], selfId?: string): GroupUserData[] => {
  return users
    .map(user => populatedUserToEscaetUser(user, selfId))
    .filter(user => !!user);
}

const groupToEscaetGroup = (group: PopulatedMeetingRoom, index: number): GroupData => {
  return {
    id: group.id,
    title: group.room.name ?? group.id,
    capacity: group.room.capacity ?? 999,
    leaderUserId: group.room.leaderUserId,
    userIds: group.room.enlistedUserIds ?? [],
    isHighlighted: group.room.highlighting === MeetingRoomHighlightingState.ONGOING,
  };
}

export const groupsToEscaetGroups = (groups: PopulatedMeetingRoom[]): GroupData[] => {
  return groups.map(groupToEscaetGroup);
}

/**
 * This hook returns group candidates,
 * Group candidates are online users who do not belong to a group yet, 
 * do not belong to a group enlisted users list.
 */
export const useEscaetGroupCandidates = (
  users: PopulatedMeetingUser[],
  selfId: string,
  onlineOnly?: boolean,
): GroupUserData[] => useMemo(
  () => {
    const candidates: PopulatedMeetingUser[] = [];
    users.forEach(user => {
      if (onlineOnly && !user.sessions.length) {
        return;
      }
      if (isRoleGreaterThanOrEqual(user.user.role, UserRole.ORGANIZER)) {
        return;
      }
      candidates.push(user);
    });
    return usersToEscaetUsers(candidates, selfId);
  }, 
  [users, selfId, onlineOnly],
);

export const useUsersToEscaetUsers = (users: PopulatedMeetingUser[], selfId: string): GroupUserData[] => useMemo(
  () => usersToEscaetUsers(users, selfId),
  [users, selfId],
);

export const useGroupsToEscaetGroups = (groups: PopulatedMeetingRoom[]): GroupData[] => useMemo(
  () => groupsToEscaetGroups(groups),
  [groups],
);

export const useEscaetGroupData = (
  groups: PopulatedMeetingRoom[],
  users: PopulatedMeetingUser[],
  selfId: string,
): { 
  groups: GroupData[], 
  users: GroupUserData[],
} => {
  const groupUsers = useMixedGroupUsers(users, groups);
  const escaetUsers = useUsersToEscaetUsers(groupUsers, selfId);
  const escaetGroups = useGroupsToEscaetGroups(groups);
  return { 
    groups: escaetGroups, 
    users: escaetUsers,
  };
};
